import React, { useId, useMemo } from "react";
import { Form, Dropdown, DropdownButton } from "react-bootstrap";
// import { BsFillFilterCircleFill } from "react-icons/bs";
import styledComponents from "styled-components";
import FallbackImage from "../Common/ImageWrapper";
import Select from "react-select";
import { useField } from "formik";
import { resolveSelectValue } from "../../utils/resolveSelectValue";

/** Popper fixed strategy often misplaces on first open; nudge a recompute. */
const FIXED_DROPDOWN_POPPER = {
  strategy: "fixed",
  onFirstUpdate: () => {
    window.dispatchEvent(new Event("scroll"));
  },
  modifiers: [
    { name: "offset", options: { offset: [0, 6] } },
    {
      name: "preventOverflow",
      options: { boundary: "viewport", padding: 8 },
    },
    {
      name: "flip",
      options: {
        fallbackPlacements: ["top-end", "bottom-end", "top", "bottom"],
      },
    },
  ],
};

// export const FormikSelectComp = ({
//   options = [],
//   fieldName,
//   labelText,
//   isRequired = false,
//   selectText = "Select",
//   desc = "",
//   labelClassName,
//   backgroundColor = "#fafafa",
// }) => {
//   const [field, meta] = useField(fieldName);
//   return (
//     <SelectCompStyle className="w-100" backgroundColor={backgroundColor}>
//       <Form.Group className="w-100">
//         {labelText && (
//           <Form.Label>
//             <p className={`mb-0 ${labelClassName}`}>
//               {labelText}
//               {isRequired && <span className={"text-danger"}>*</span>}
//             </p>
//           </Form.Label>
//         )}
//         <Form.Select {...field}>
//           <option className="" value={""}>
//             {selectText}
//           </option>
//           {options.map((option, index) => (
//             <option value={option.value} key={index}>
//               {option.label}
//             </option>
//           ))}
//         </Form.Select>
//         {desc && <Form.Text className="text-muted">{desc}</Form.Text>}
//         {meta.touched &&
//           meta.error && ( // Display validation error if touched and error exists
//             <small className="text-danger mt-2">{meta.error.toString()}</small>
//           )}
//       </Form.Group>
//     </SelectCompStyle>
//   );
// };

export const FormikSelectComp = ({
  options = [],
  fieldName,
  labelText,
  isRequired = false,
  selectText = "Select",
  desc = "",
  labelClassName,
  backgroundColor = "#fafafa",
  onValueChange,
  isSearchable = true, // Enable search by default
  ...props
}) => {
  const [field, meta, helpers] = useField(fieldName); // Use helpers for manual control
  const { setValue, setTouched } = helpers;

  // Filter out invalid options and ensure they have value and label
  const validOptions = React.useMemo(() => {
    if (!Array.isArray(options)) return [];
    return options.filter(
      (option) =>
        option &&
        option.value !== undefined &&
        option.value !== null &&
        option.label !== undefined,
    );
  }, [options]);

  // Find the selected option object based on field.value
  const selectedOption = React.useMemo(() => {
    if (!field.value || validOptions.length === 0) return null;
    return (
      validOptions.find(
        (option) => String(option.value) === String(field.value),
      ) || null
    );
  }, [field.value, validOptions]);

  const handleChange = (selectedOption) => {
    const selectedValue = selectedOption ? selectedOption.value : "";
    setValue(selectedValue); // Update Formik's value manually
    setTouched(true); // Mark field as touched
    if (onValueChange) {
      onValueChange(selectedValue);
    }
  };

  const handleBlur = () => {
    setTouched(true);
  };

  return (
    <SelectCompStyle className="w-100" backgroundColor={backgroundColor}>
      <Form.Group className="w-100">
        {labelText && (
          <Form.Label>
            <p className={`mb-0 ${labelClassName}`}>
              {labelText}
              {isRequired && <span className={"text-danger"}>*</span>}
            </p>
          </Form.Label>
        )}
        <Select
          styles={{
            control: (baseStyles, state) => ({
              ...baseStyles,
              backgroundColor: backgroundColor || "#fafafa",
              borderColor: state.isFocused ? "var(--color-link)" : "#A1A0A0",
              borderWidth: "1px",
              boxShadow: state.isFocused
                ? "0 0 0 0.2rem color-mix(in srgb, var(--color-primary) 25%, transparent)"
                : "none",
              "&:hover": {
                borderColor: state.isFocused ? "var(--color-link)" : "#A1A0A0",
              },
            }),
            option: (baseStyles, state) => ({
              ...baseStyles,
              backgroundColor: state.isSelected
                ? "var(--color-primary)"
                : state.isFocused
                ? "var(--color-primary-soft)"
                : "white",
              color: state.isSelected ? "white" : "#45464e",
              "&:active": {
                backgroundColor: "var(--color-primary)",
                color: "white",
              },
            }),
            placeholder: (baseStyles) => ({
              ...baseStyles,
              color: "#6c757d",
            }),
          }}
          value={selectedOption}
          onChange={handleChange}
          onBlur={handleBlur}
          options={validOptions}
          placeholder={selectText}
          isSearchable={isSearchable}
          isClearable={false}
          noOptionsMessage={() => "No options available"}
          classNamePrefix="react-select"
          className="react-select-container"
          {...props}
        />
        {desc && <Form.Text className="text-muted">{desc}</Form.Text>}
        {meta.touched &&
        meta.error && ( // Display validation error if touched and error exists
            <small className="text-danger mt-2">{meta.error.toString()}</small>
          )}
      </Form.Group>
    </SelectCompStyle>
  );
};

export const SelectComp = ({
  LabelText,
  labelText,
  selectClassName,
  LabelClassName,
  arrayComp = [],
  selectText,
  input,
  inputValue,
  required,
  desc,
  errors,
  borderColor,
  borderWidth,
  backgroundColor = "#fafafa",
  ...props
}) => {
  return (
    <SelectCompStyle
      className="w-100"
      borderColor={borderColor}
      borderWidth={borderWidth}
      backgroundColor={backgroundColor}
    >
      <Form.Group className="w-100">
        {(LabelText || labelText) && (
          <Form.Label>
            <p className={`mb-0 ${LabelClassName}`}>
              {LabelText || labelText}
              {required && <span className={"text-danger"}>*</span>}
            </p>
          </Form.Label>
        )}
        <Form.Select className={` ${selectClassName}`} {...props}>
          <option className="" value={""}>
            {selectText}
          </option>
          {arrayComp?.length > 0 &&
            arrayComp?.map((item, index) => (
              <option key={index} value={input ? item[inputValue] : item}>
                {input ? item[input] : item}
              </option>
            ))}
        </Form.Select>
        {desc && <small>{desc}</small>}
        {errors?.map((error, i) => (
          <small className="text-danger mt-2" key={i}>
            {error}
          </small>
        ))}
      </Form.Group>
    </SelectCompStyle>
  );
};
export const SelectComp2 = ({
  LabelText,
  labelText,
  selectClassName,
  LabelClassName,
  arrayComp = [],
  selectText,
  input,
  inputValue,
  required,
  desc,
  errors,
  borderColor,
  borderWidth,
  selectedOption,
  setSelectedOption = () => {},
  placeholder,
  isMulti,
  defaultMenuIsOpen,
  backgroundColor = "#fafafa",
  isLoading,
  isSearchable = false,
  ...props
}) => {
  const options = React.useMemo(() => {
    if (!Array.isArray(arrayComp)) return [];
    if (input && inputValue) {
      return arrayComp.map((item) => ({
        ...item,
        label: item[input],
        value: item[inputValue],
      }));
    }
    return arrayComp;
  }, [arrayComp, input, inputValue]);

  const isControlled = selectedOption !== undefined;
  const value = React.useMemo(
    () =>
      isControlled
        ? resolveSelectValue(selectedOption, options, Boolean(isMulti))
        : undefined,
    [isControlled, isMulti, selectedOption, options]
  );

  return (
    <SelectCompStyle
      className="w-100"
      borderColor={borderColor}
      borderWidth={borderWidth}
    >
      <Form.Group className="w-100">
        {(LabelText || labelText) && (
          <Form.Label>
            <p className={`mb-0 ${LabelClassName}`}>
              {LabelText || labelText}
              {required && <span className={"text-danger"}>*</span>}
            </p>
          </Form.Label>
        )}
        <Select
          styles={{
            control: (baseStyles, state) => ({
              ...baseStyles,
              backgroundColor: backgroundColor || "#fafafa",
            }),
          }}
          onChange={setSelectedOption}
          options={options}
          isSearchable={isSearchable}
          isMulti={isMulti ? true : false}
          classNamePrefix="px-5"
          defaultMenuIsOpen={defaultMenuIsOpen}
          placeholder={placeholder}
          isLoading={isLoading}
          getOptionValue={(option) => option?.value}
          {...props}
          {...(isControlled ? { value } : {})}
        />
        {desc && <small>{desc}</small>}
        {errors?.map((error, i) => (
          <small className="text-danger mt-2 d-block" key={i}>
            {error}
          </small>
        ))}
      </Form.Group>
    </SelectCompStyle>
  );
};

export const SelectCompWithFormattedLabel = ({
  LabelText,
  labelText,
  selectClassName,
  LabelClassName,
  arrayComp = [],
  selectText,
  input,
  inputValue,
  required,
  desc,
  errors,
  borderColor,
  borderWidth,
  selectedOption,
  setSelectedOption = null,
  placeholder,
  isMulti,
  defaultMenuIsOpen,
  fieldName,
  setOption,
  menuPortalTarget,
  menuPosition,
  ...props
}) => {
  const isControlled = selectedOption !== undefined;
  const resolvedValue = React.useMemo(
    () =>
      isControlled
        ? resolveSelectValue(selectedOption, arrayComp, Boolean(isMulti))
        : undefined,
    [isControlled, selectedOption, arrayComp, isMulti]
  );

  const handleChange = (nextOption) => {
    if (setOption) {
      setOption(nextOption);
    } else if (setSelectedOption) {
      setSelectedOption(nextOption);
    }
  };

  const formatOptionLabel = ({ photo, name, userId, value, label }) => {
    // Prefer membership number (userId); never show internal numeric id as membership no
    const displayId = userId || "N/A";
    const displayName = name || label || "N/A";
    return (
      <div className="d-flex align-items-center">
        {photo && (
          <FallbackImage
            className="rounded-circle me-3"
            width="24px"
            height="24px"
            src={photo}
          />
        )}
        <span>{`${displayName} | Membership No: ${displayId}`}</span>
      </div>
    );
  };

  return (
    <SelectCompStyle
      className={`w-100`}
      borderColor={borderColor}
      borderWidth={borderWidth}
    >
      <Form.Group className="w-100">
        {(LabelText || labelText) && (
          <Form.Label>
            <p className={`mb-0 ${LabelClassName}`}>
              {LabelText || labelText}
              {required && <span className={"text-danger"}>*</span>}
            </p>
          </Form.Label>
        )}
        <Select
          styles={{
            control: (baseStyles, state) => ({
              ...baseStyles,
              backgroundColor: "#fafafa",
            }),
            ...(props.styles || {}),
          }}
          options={arrayComp}
          isSearchable={true}
          isMulti={isMulti ? true : false}
          classNamePrefix="px-5"
          defaultMenuIsOpen={defaultMenuIsOpen}
          placeholder={placeholder}
          onChange={handleChange}
          formatOptionLabel={formatOptionLabel}
          menuPortalTarget={menuPortalTarget}
          menuPosition={menuPosition}
          {...props}
          {...(isControlled ? { value: resolvedValue } : {})}
        />
        {desc && <small>{desc}</small>}
        {errors?.map((error, i) => (
          <small className="text-danger mt-2" key={i}>
            {error}
          </small>
        ))}
      </Form.Group>
    </SelectCompStyle>
  );
};

const SelectCompStyle = styledComponents.div`
select{
  border-width: ${(props) =>
    props?.borderWidth ? props?.borderWidth : "1px"} !important;
    border-color: ${(props) =>
      props?.borderColor ? props?.borderColor : "#A1A0A0"} !important;
    
}
select {
  background-color: ${(props) =>
    props?.backgroundColor ? props?.backgroundColor : "#FAFAFA"} !important;
}
/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type=number] {
  -moz-appearance: textfield;
}
.px-5__control{
  padding-top: 0.2rem!important;
    padding-bottom: 0.2rem!important;
}

/* React Select Styling for FormikSelectComp */
.react-select-container {
  font-size: 14px;
}

.react-select__control {
  min-height: 38px !important;
  border-radius: 5px !important;
  padding-top: 0.2rem !important;
  padding-bottom: 0.2rem !important;
}

.react-select__value-container {
  padding: 0 8px !important;
}

.react-select__input-container {
  margin: 0 !important;
  padding: 0 !important;
}

.react-select__indicator-separator {
  display: none !important;
}

.react-select__dropdown-indicator {
  padding: 4px 8px !important;
  color: #45464e !important;
}

.react-select__menu {
  border-radius: 5px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  margin-top: 4px !important;
  z-index: 9999 !important;
}

.react-select__menu-list {
  padding: 4px 0 !important;
}

.react-select__option {
  padding: 8px 12px !important;
  cursor: pointer !important;
  font-size: 14px !important;
}

.react-select__placeholder {
  color: #6c757d !important;
  font-size: 14px !important;
}

.react-select__single-value {
  color: #45464e !important;
  font-size: 14px !important;
}

/* Search input styling */
.react-select__input {
  color: #45464e !important;
  font-size: 14px !important;
}

`;

export const DropDownComp = ({
  arrayComp = [],
  DropDownText,
  input,
  row,
  show,
  dropLabelClassName = "",
  drop = "down",
  ItemClassName = "",
  borderColor,
  dropdownId,
  dropdownShow,
  onDropdownToggle,
  align,
}) => {
  // Stable id — Math.random() on every render broke Popper's first placement.
  const reactId = useId();
  const uniqueId = useMemo(
    () => dropdownId || `dropdown${reactId.replace(/:/g, "")}`,
    [dropdownId, reactId],
  );

  const isControlled = dropdownShow !== undefined && onDropdownToggle;

  return (
    <DropDownStyle Hide={show} BorderColor={borderColor}>
      <Dropdown
        className="border-0 p-0"
        drop={drop}
        {...(align ? { align } : {})}
        {...(isControlled && {
          show: dropdownShow,
          onToggle: onDropdownToggle,
        })}
      >
        <Dropdown.Toggle
          variant=""
          id={uniqueId}
          className={`p-0 ${dropLabelClassName}`}
          style={{
            borderColor: "transparent",
            backgroundColor: "transparent",
          }}
        >
          {DropDownText}
        </Dropdown.Toggle>

        {arrayComp.length > 0 && (
          <Dropdown.Menu
            renderOnMount
            className="border-0 py-2 px-2 shadow table-action-dropdown-menu"
            popperConfig={FIXED_DROPDOWN_POPPER}
            style={{
              minWidth: "160px",
              zIndex: 2000,
            }}
          >
            {arrayComp.map((item, index) => {
              const isDisabled =
                typeof item?.disabled === "function"
                  ? Boolean(item.disabled(row))
                  : Boolean(item?.disabled);

              return (
              <Dropdown.Item
                as="button"
                type="button"
                disabled={isDisabled}
                className={`mb-1 ${ItemClassName} ${item?.aClassName || ""} ${
                  isDisabled ? "opacity-75" : ""
                }`}
                key={index}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (item?.action && !isDisabled) {
                    item.action(row);
                  }
                }}
              >
                {item?.icon && (
                  <span className="pe-2 d-inline-flex align-items-center">
                    {typeof item.icon === "string" ? (
                      <img src={item.icon} alt="" />
                    ) : (
                      item.icon
                    )}
                  </span>
                )}
                {typeof item[input] === "string" ? (
                  <span className="h50">{item[input]}</span>
                ) : (
                  item[input]
                )}
              </Dropdown.Item>
              );
            })}
          </Dropdown.Menu>
        )}
        {arrayComp.length <= 0 && (
          <Dropdown.Menu
            renderOnMount
            className="border-0 py-3 px-2 table-action-dropdown-menu"
            popperConfig={FIXED_DROPDOWN_POPPER}
          >
            <Dropdown.Item className="border-0" disabled>
              Nothing Yet
            </Dropdown.Item>
          </Dropdown.Menu>
        )}
      </Dropdown>
    </DropDownStyle>
  );
};

export const DropDownComp2 = ({
  arrayComp,
  DropDownText,
  DropDownBody,
  input,
  show,
  dropLabelClassName,
  drop,
}) => {
  return (
    <DropDownStyle Hide={show}>
      <Dropdown className="border-0 p-0">
        <Dropdown.Toggle
          variant=""
          id="dropdown-basic"
          className={` p-0 ${dropLabelClassName}`}
          drop={drop}
        >
          {DropDownText || DropDownBody}
        </Dropdown.Toggle>

        <Dropdown.Menu className="border-0 py-3 px-2 shadow">
          {arrayComp}
        </Dropdown.Menu>
      </Dropdown>
    </DropDownStyle>
  );
};

const DropDownStyle = styledComponents.div`
.dropdown-toggle::after {
  display:${(prop) => (prop.Hide ? prop.Hide : "none")};
}
.dropdown-item:focus {
   background: var(--color-primary) !important;
  color: white !important;
}
a:focus, a:active, a:hover{
   background: var(--color-primary) !important;
  color: white !important;
}

.dropdown-toggle{
  border-color:${(props) =>
    props?.BorderColor ? props?.BorderColor : "transparent"} !important;
}

/* Let Popper control placement (fixed strategy escapes table overflow). */
.dropdown-menu.table-action-dropdown-menu {
  z-index: 2000 !important;
  min-width: 160px;
  margin-top: 0;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
  border-radius: 10px !important;
}
`;

export const DropDownComp3 = ({
  autoClose,
  Transform = null,
  arrayComp = [],
  bodyComp,
  DropDownText,
  input,
  row,
  show,
  dropLabelClassName = "",
  ItemClassName2 = "",
  drop,
  ItemClassName = "",
  borderColor,
}) => {
  return (
    <DropDown3Style Hide={show} BorderColor={borderColor} Transform={Transform}>
      <DropdownButton
        autoClose={autoClose}
        drop={drop}
        variant=""
        className={dropLabelClassName}
        id="dropdown-item-button"
        title={DropDownText}
      >
        {arrayComp?.map((item, i) => (
          <div key={i}>
            <Dropdown.Item
              className={!item?.select ? ItemClassName : ItemClassName2}
              as="button"
            >
              {item?.name}
            </Dropdown.Item>
          </div>
        ))}
        {bodyComp}
      </DropdownButton>
    </DropDown3Style>
  );
};
const DropDown3Style = styledComponents.div`
display:inline-block;
.dropdown-toggle::after {
  display:${(prop) => (prop.Hide ? prop.Hide : "none")};
}
.dropdown-item::focus {
   background: red !important;
  color: white !important;
}
a:focus,a:active,a:hover{
   background: var(--color-primary) !important;
  color: white !important;
}
.dropdown-menu{
  border:none;
  top: 18px !important;
  padding:10px;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.05);
  // transform: translate3d(-181px, 41.5px, 0px) !important;
}
.dropdown-toggle{
  border-color:${(props) =>
    props?.BorderColor ? props?.BorderColor : "inherit"} !important;
}

.dropdown-item{
  padding:0;
}
button{
  border:none;
}

@media only screen and (max-width: 400px) {
  .dropdown-menu.show{
    transform: ${(props) => props?.Transform} !important;
    // transform: translate3d(-172px, 59.5px, -11px)!important;
  }
}

`;
